/**
 * NotificationCoinsDateLine
 *
 * A subtitle line of the shape `{amount} <coin-icon> · {date}`, ported from the
 * coins+date `Row` shared by the pay-premium / pay-boost video items
 * (`notification_pay_premium_video_item.dart:86-124`) and reused as the second
 * line of the gift / unlock subtitles. The dot separator + date mirror Flutter's
 * 2px `disabledColor` divider followed by `getNotificationDate`.
 */
import React from 'react';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface NotificationCoinsDateLineProps {
  /** Pre-formatted coin amount string. */
  amount: string;
  /** Pre-formatted, localized date string. */
  date: string;
  /** Optional leading prefix (e.g. the localized "Total: " prefix). */
  prefix?: string;
}

export const NotificationCoinsDateLine: React.FC<NotificationCoinsDateLineProps> = ({
  amount,
  date,
  prefix,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: responsive.gap.xs,
        color: theme.text.secondary,
        fontSize: responsive.font.sm,
      }}
    >
      <span>
        {prefix}
        {amount}
      </span>
      <AssetIcon
        path="/assets/images/coins.png"
        width={responsive.icon.xs}
        height={responsive.icon.xs}
        color={theme.exclusive}
      />
      <span style={{ color: theme.disabled }}>·</span>
      <span style={{ color: theme.disabled }}>{date}</span>
    </div>
  );
};
